import { useEffect, useState } from 'react';
import { Modal, Form, Select, DatePicker, InputNumber, Button, Row, Col, Input, Typography, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';

interface Props {
  open: boolean;
  onClose: () => void;
  /** If provided, pre-fills the retailer. */
  retailerId?: string;
}

const MODES = ['CASH', 'CHEQUE', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'CARD', 'OTHER'];
const inr = (n: any) => `₹${Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Staff-direct payment record (auto-approved — posts the ledger immediately). Order-based: optionally
 * link the payment to one of the retailer's orders to reduce that order's due (invoice allocation is
 * retired). Leave the order blank for an on-account / advance payment.
 */
export function PaymentFormModal({ open, onClose, retailerId }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const [retailer, setRetailer] = useState<string | undefined>(retailerId);

  useEffect(() => {
    if (open) {
      setRetailer(retailerId);
      form.resetFields();
      form.setFieldsValue({ paymentDate: dayjs(), mode: 'UPI', retailerId });
    }
  }, [open, retailerId, form]);

  const { data: retailers } = useQuery({
    queryKey: ['retailers-for-payment'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 500 } }),
    enabled: open,
  });

  // The selected retailer's orders that still have a due — optional order link.
  const { data: orders } = useQuery({
    queryKey: ['retailer-due-orders', retailer],
    queryFn: () => request<any>({ url: '/orders', params: { retailerId: retailer, pageSize: 100 } }),
    enabled: open && !!retailer,
  });
  const dueOrders = ((orders?.data ?? orders ?? []) as any[])
    .filter((o) => Number(o.invoiceBalance) > 0.01 && !['DRAFT', 'CANCELLED', 'REJECTED', 'CLOSED'].includes(o.status));

  const record = useMutation({
    mutationFn: (vals: any) => request<any>({
      url: '/payments', method: 'POST',
      data: {
        retailerId: vals.retailerId,
        orderId: vals.orderId || undefined,
        paymentDate: vals.paymentDate.format('YYYY-MM-DD'),
        amount: vals.amount,
        mode: vals.mode,
        referenceNo: vals.referenceNo,
        utr: vals.utr,
        attachmentUrls: vals.attachmentUrls,
      },
    }),
    onSuccess: (r) => {
      message.success(`Payment ${r.paymentNo} recorded`);
      qc.invalidateQueries({ queryKey: ['payments'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['retailer-ledger'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} width={640} title="Record Payment">
      <Form form={form} layout="vertical" onFinish={(v) => record.mutate(v)} initialValues={{ paymentDate: dayjs(), mode: 'UPI', retailerId }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="retailerId" label="Retailer" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                onChange={(v) => { setRetailer(v); form.setFieldsValue({ orderId: undefined }); }}
                options={(retailers?.data ?? []).map((r: any) => ({ value: String(r.id), label: `${r.code} · ${r.companyName}` }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="orderId" label="Against order (optional)" tooltip="Link to an order to reduce its due. Leave blank for an on-account / advance payment.">
              <Select
                allowClear
                placeholder={!retailer ? 'Pick a retailer first' : dueOrders.length ? 'Select an order with a due' : 'No outstanding orders'}
                disabled={!retailer}
                onChange={(oid) => { const o = dueOrders.find((x) => String(x.id) === String(oid)); if (o) form.setFieldsValue({ amount: Number(o.invoiceBalance) }); }}
                options={dueOrders.map((o) => ({ value: String(o.id), label: `${o.orderNo} · ${inr2(o.invoiceBalance)} due` }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}><Form.Item name="paymentDate" label="Date" rules={[{ required: true }]}><DatePicker className="w-full" /></Form.Item></Col>
          <Col span={8}><Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}><InputNumber className="w-full" min={1} step={100} /></Form.Item></Col>
          <Col span={8}><Form.Item name="mode" label="Mode" rules={[{ required: true }]}><Select options={MODES.map((m) => ({ value: m }))} /></Form.Item></Col>
          <Col span={12}><Form.Item name="referenceNo" label="Reference No"><Input placeholder="Cheque / UPI ref" /></Form.Item></Col>
          <Col span={12}><Form.Item name="utr" label="UTR / Bank Ref"><Input /></Form.Item></Col>
          <Col span={24}>
            <Form.Item
              name="attachmentUrls"
              label="Payment proof (receipts)"
              rules={[{ required: true, type: 'array', min: 1, message: 'Upload at least one receipt / proof' }]}
              extra="Required — receipts, cheque images, or transfer screenshots (images or PDF)."
            >
              <MultiFileUploadField category="receipt" buttonLabel="Upload receipts" />
            </Form.Item>
          </Col>
        </Row>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          Recorded by staff — posts to the ledger immediately (no approval step).
        </Typography.Paragraph>
        <Button variant="primary" type="submit" loading={record.isPending} block>Record payment</Button>
      </Form>
    </Modal>
  );
}
